/**
 * CalendarToolCard — carte Google Calendar dans le Bureau cockpit.
 *
 * Extrait de Bureau.jsx (Batch #14.221).
 */
import { useState } from "react";
import { Calendar, ExternalLink, Loader2, Plus, Link2 } from "lucide-react";
import WorkspaceLinkForm from "./WorkspaceLinkForm";

export default function CalendarToolCard({ hub, busy, onCreate, masterId, expertise, onLinked }) {
  const cal = hub?.calendar || {};
  const [editing, setEditing] = useState(false);

  if (cal.calendar_id) {
    return (
      <div className="border border-emerald-200 hover:border-emerald-400 rounded-xl p-4 bg-white hover:shadow-md min-h-[140px] transition" data-testid="bureau-calendar">
        <div className="flex items-start justify-between">
          <Calendar className="w-6 h-6 text-emerald-600" />
          <button
            onClick={() => setEditing(true)}
            className="text-[10px] text-slate-400 hover:text-emerald-600 inline-flex items-center gap-0.5"
            title="Re-lier vers un autre calendrier"
            data-testid="bureau-calendar-edit-btn"
          >
            <Link2 className="w-3 h-3" /> Modifier
          </button>
        </div>
        <div className="font-semibold text-sm text-slate-800 mt-3">Google Calendar</div>
        <div className="text-[11px] text-slate-500 mt-0.5 truncate">
          {cal.linked_manually ? "Lié manuellement" : "Auto-créé"}
        </div>
        <a
          href={cal.calendar_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-emerald-600 hover:text-emerald-800"
          data-testid="bureau-calendar-open-link"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Ouvrir le calendrier
        </a>
        {editing && (
          <WorkspaceLinkForm
            tool="calendar"
            masterId={masterId}
            expertise={expertise}
            initialUrl={cal.calendar_url || ""}
            placeholder="https://calendar.google.com/calendar/embed?src=...  ou  cal-id@group.calendar.google.com"
            tip="Créez un calendrier dans calendar.google.com → Paramètres → ID du calendrier → copiez-le ici."
            onCancel={() => setEditing(false)}
            onSaved={() => { setEditing(false); onLinked?.(); }}
          />
        )}
      </div>
    );
  }
  return (
    <div
      className="border border-dashed border-emerald-300 rounded-xl p-4 bg-emerald-50/30 min-h-[140px]"
      data-testid="bureau-calendar-empty"
    >
      <Calendar className="w-6 h-6 text-emerald-400" />
      <div className="font-semibold text-sm text-slate-700 mt-3">Google Calendar</div>
      <div className="text-[11px] text-slate-500 mt-0.5">Aucun calendrier — création ou liaison manuelle</div>
      {cal.error && (
        <div className="text-[10px] text-amber-700 mt-1 truncate" title={cal.error}>
          ⚠ {cal.error.slice(0, 40)}…
        </div>
      )}
      {!editing ? (
        <div className="mt-3 flex items-center gap-1.5 flex-wrap">
          <button
            onClick={onCreate}
            disabled={busy}
            className="px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-xs inline-flex items-center gap-1.5 hover:bg-emerald-700 disabled:opacity-40"
            data-testid="bureau-calendar-create-btn"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Créer auto
          </button>
          <button
            onClick={() => setEditing(true)}
            className="px-2.5 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 bg-white text-xs inline-flex items-center gap-1.5 hover:bg-emerald-50"
            data-testid="bureau-calendar-link-btn"
          >
            <Link2 className="w-3.5 h-3.5" /> Lier
          </button>
        </div>
      ) : (
        <WorkspaceLinkForm
          tool="calendar"
          masterId={masterId}
          expertise={expertise}
          initialUrl=""
          placeholder="https://calendar.google.com/calendar/embed?src=...  ou  cal-id@group.calendar.google.com"
          tip="Créez un calendrier dans calendar.google.com → Paramètres → ID du calendrier → copiez-le ici."
          onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); onLinked?.(); }}
        />
      )}
    </div>
  );
}
